import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import GoogleSignInButton from "../components/GoogleSignInButton";
import SEO from "../components/SEO";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [showEmail, setShowEmail] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back");
      nav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="login-page">
      <SEO title="Sign In" description="Sign in to your Weddinsite account." path="/login" noindex />
      <Navbar />
      <div className="px-6 py-12 md:py-16 max-w-md mx-auto">
        <div className="text-[10px] uppercase tracking-[0.4em] text-stone-500 mb-3">Welcome back</div>
        <h1 className="font-serif-display text-4xl md:text-5xl font-light mb-8">Sign in.</h1>

        <GoogleSignInButton next={next} label="Continue with Google" testId="login-google-btn" />

        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-stone-400">
          <div className="flex-1 h-px bg-stone-200" />
          <span>or</span>
          <div className="flex-1 h-px bg-stone-200" />
        </div>

        {!showEmail ? (
          <button
            type="button"
            onClick={() => setShowEmail(true)}
            data-testid="login-show-email"
            className="w-full py-3 text-xs uppercase tracking-[0.3em] border border-stone-300 text-stone-700 hover:bg-stone-100"
          >
            Sign in with email
          </button>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="login-email-form">
            <input data-testid="login-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm" />
            <input data-testid="login-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm" />
            <button data-testid="login-submit" disabled={busy} className="w-full py-3 bg-stone-900 text-white uppercase tracking-[0.3em] text-xs hover:bg-stone-800 disabled:opacity-60">{busy ? "Signing in..." : "Sign in"}</button>
            <div className="text-sm"><Link to="/forgot-password" className="text-stone-600 hover:text-stone-900 underline underline-offset-4" data-testid="goto-forgot">Forgot password?</Link></div>
          </form>
        )}

        <div className="mt-8 text-sm text-stone-600">
          New here? <Link to="/signup" className="underline underline-offset-4 font-semibold" data-testid="goto-signup">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
